<template>
  <div class="d-flex flex-column ga-4">
    <div class="d-flex flex-md-row flex-column align-center">
      <v-col
        class="pa-0 pe-4 pe-sm-0 mb-4 mb-md-0"
        cols="12"
        md="3"
      >
        <!-- eslint-disable-next-line prefer-standard-components/no-vuetify-inputs -- TODO migrate to LabeledSelect -->
        <v-select
          v-model="selectedProvider"
          @update:model-value="loadWaitlistOffers"
          :items="providers"
          :loading="processing"
          density="compact"
          item-title="name"
          item-value="id"
          variant="solo-filled"
          flat
          hide-details
          return-object
        />
      </v-col>

      <v-col
        class="pa-0"
        cols="12"
        md="3"
      >
        <!-- TODO -->
        <!-- <v-text-field
          v-model="query"
          :aria-label="t('Search waitlist requests by name')"
          :placeholder="t('Search waitlist requests by name')"
          density="compact"
          prepend-inner-icon="search"
          variant="solo-filled"
          flat
          hide-details
        /> -->
      </v-col>

      <v-col
        class="pa-0 d-flex justify-md-end justify-start"
        cols="12"
        md="6"
      >
        <v-btn
          @click="createWaitlistRequestDialog.open()"
          class="w-100 w-md-auto"
          color="primary"
          prepend-icon="add"
          variant="flat"
        >
          {{ $bct('Add child to waitlist') }}
        </v-btn>
      </v-col>
    </div>

    <DataTableServer
      v-if="items"
      @update:page="handlePageChange"
      :headers="tableHeaders"
      :hide-default-footer="!totalItemCount"
      :items="items"
      :items-length="totalItemCount"
      :items-per-page="pageSize"
      :loading="processing"
      class="b-radius-0 b-1 bc-outlined-gray"
      item-key="id"
      no-data-text="No waitlist requests found"
      fixed-header
      return-object
      show-select
    >
      <template #[`item.childName`]="{ item }">
        <span> {{ item.child_first_name }} {{ item.child_last_name }} </span>
      </template>

      <template #[`item.childDOB`]="{ item }">
        <LongDate :date="item.child_birthdate" />
      </template>

      <template #[`item.parentGuardianName`]="{ item }">
        {{ item.parent_first_name }} {{ item.parent_last_name }}
      </template>

      <template #[`item.parent_email`]="{ item }">
        <a
          v-if="item.parent_email"
          v-text="item.parent_email"
          :href="'mailto:' + item.parent_email"
        />
      </template>

      <template #[`item.parent_phone_number`]="{ item }">
        {{ formatPhoneNumber(item.parent_phone_number) }}
      </template>

      <template #[`item.created_at`]="{ item }">
        <LongDate :date="item.created_at" />
      </template>

      <template #[`item.actions`]="{ item }">
        <v-btn
          @click="deleteWaitlistRequest(item.id)"
          data-testid="delete-waitlist-request"
          size="small"
          variant="text"
          icon
        >
          <v-icon
            class="material-icons-outlined"
            icon="delete"
          />
        </v-btn>
      </template>
    </DataTableServer>

    <ResourceDialog
      @save="createWaitlistRequest"
      ref="createWaitlistRequestDialog"
      :fields="newFields"
      :max-width="600"
      :processing="processing"
      title="New waitlist request"
    />

    <ConfirmDialog ref="confirmDialog" />
  </div>
</template>

<script setup>
import ConfirmDialog from '@/shared/components/ConfirmDialog.vue';
import DataTableServer from '@/shared/components/table/DataTableServer.vue';
import LongDate from '@/shared/components/LongDate.vue';
import ResourceDialog from '@/shared/components/form/ResourceDialog.vue';
import useApi from '@/shared/composables/useApi';
import useEventBus from '@/shared/composables/useEventBus';

const { api } = useApi();
const eventBus = useEventBus();

const confirmDialog = ref(null);
const createWaitlistRequestDialog = ref(null);
const items = ref([]);
const page = ref(1);
const pageSize = ref(25);
const processing = ref(false);
// const query = ref(null);
const selectedProvider = ref(null);
const tableHeaders = ref([
  {
    title: 'Child name',
    value: 'childName',
  },
  {
    title: 'Child DOB',
    value: 'childDOB',
  },
  {
    title: 'Parent/Guardian name',
    value: 'parentGuardianName',
  },
  {
    title: 'Email',
    value: 'parent_email',
  },
  {
    title: 'Phone number',
    value: 'parent_phone_number',
  },
  {
    title: 'Request date',
    value: 'created_at',
  },
  {
    title: '',
    value: 'actions',
    align: 'end',
  },
]);
const totalItemCount = ref(null);

const $bct = inject('translator');

const props = defineProps({
  providers: {
    type: Array,
    default: () => [],
  },
});

watch(
  () => props.providers,
  async (newValue, oldValue) => {
    if (!newValue || newValue === oldValue) return;

    if (newValue.length > 0) {
      const sortedProviders = [...newValue].sort((a, b) => (a.name > b.name ? 1 : -1));
      const firstProvider = sortedProviders[0];

      // Skip if the provider is already selected
      if (selectedProvider.value?.id === firstProvider.id) return;

      selectedProvider.value = firstProvider;
      await loadWaitlistOffers();
    } else {
      selectedProvider.value = null;
    }
  },
  { immediate: true },
);
const newFields = computed(() => {
  return [
    {
      cols: '6',
      text: 'Parent first name',
      value: 'parent_first_name',
      required: true,
    },
    {
      cols: '6',
      text: 'Parent last name',
      value: 'parent_last_name',
      required: true,
    },
    {
      text: 'Parent email',
      value: 'parent_email',
      required: true,
    },
    {
      text: 'Parent phone',
      value: 'parent_phone_number',
      mask: '(###) ###-####',
    },
    {
      cols: '6',
      text: 'Child first name',
      value: 'child_first_name',
      required: true,
    },
    {
      cols: '6',
      text: 'Child last name',
      value: 'child_last_name',
      required: true,
    },
    {
      text: 'Child DOB',
      value: 'child_birthdate',
      required: true,
      type: 'dob',
    },
    {
      text: 'Location',
      value: 'provider_id',
      required: true,
      items: props.providers,
      itemText: 'name',
      itemValue: 'id',
    },
  ];
});

async function createWaitlistRequest(waitlistRequest) {
  processing.value = true;

  const response = await api.organization.waitlistOffer.create(waitlistRequest);
  if (response?.status !== 201) {
    processing.value = false;
    return;
  }

  eventBus.chime('Created');
  createWaitlistRequestDialog.value.close();
  await loadWaitlistOffers();
}

async function deleteWaitlistRequest(id) {
  const confirm = await confirmDialog.value.confirmWithText(
    'Are you sure you want to delete this waitlist request?',
  );
  if (!confirm) return;

  processing.value = true;

  const response = await api.organization.waitlistOffer.destroy(id);
  if (response?.status !== 202) {
    processing.value = false;
    return;
  }

  eventBus.chime('Deleted');
  await loadWaitlistOffers();
}

function formatPhoneNumber(phoneNumber) {
  if (!phoneNumber) return '';
  return phoneNumber.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3');
}

async function handlePageChange(newPageValue) {
  page.value = newPageValue;
  await loadWaitlistOffers();
}

async function loadWaitlistOffers() {
  processing.value = true;

  const params = {
    page: page.value,
    provider_id: selectedProvider.value?.id,
  };
  const response = await api.organization.waitlistOffer.index(params);
  items.value = response?.data;
  totalItemCount.value = parseInt(response?.headers?.['x-total-count'], 10);

  processing.value = false;
}
</script>
